/* eslint-disable @typescript-eslint/naming-convention */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, Directive, effect, HostListener, inject, input, linkedSignal, NgZone, OnDestroy, output, signal, untracked } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { INITIAL_TIME_STAMP, KeyCodes, PRODUCTION_COMMAND_BUTTONS } from '@src/suite/common/enums/constants';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { ProductionCommandDialogComponent } from '@src/suite/common/st-full-screen-dialog/production-command-dialog/production-command-dialog';
import { selectEditorApiKey } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { AnchorCommand, DurationCommand, InsertCommand, NotesCommand, ProductionCommand, ReadRateCommand, TakeCommand } from '@src/suite/common/store/story/models/story.models';
import { convertHtmlToSimplifiedHtml } from '@src/suite/common/utils/html-simplify.utils';
import {
    findParentTag,
    ML2HtmlUtils,
    PRODUCTION_CONTENT_ID_PREFIX,
    ProductionCommandsLangKey,
    ProductionCommandType,
    ProductionFieldName,
    resolvePointerPastClosedCommand,
} from '@src/suite/common/utils/ml-2-html-utils';
import { parseProductionCommand } from '@src/suite/common/utils/story-utils';
import { convertDurationToENPSClientFormat } from '@src/suite/common/utils/timing-format';
import { KeyName } from '@src/suite/common/utils/util';
import { materializeNeutralizedMedia, parseXml } from '@src/suite/common/utils/xml-utils';
import FroalaEditor from 'froala-editor';
import { DateTime } from 'luxon';
import { interval, Observable, Subject } from 'rxjs';
import { catchError, filter, finalize, map, take, takeUntil, tap, throttle } from 'rxjs/operators';
import { v4 } from 'uuid';

import { DragEventKeys, DRAGTYPES, EditorActions } from '../../enums/enums';
import { generateItemIDbyObjId } from '../../utils/mos-utlis';
import { SidecarSelectorComponent } from '../st-rich-text-editor/sidecar-selector/sidecar-selector';
import { HTML_ALLOWED_EMPTY_TAGS, htmlAllowedAttrs } from '../st-rich-text-editor/st-rich-text-editor';

const SIDECAR_SELECTOR_POSITIONS: ConnectedPosition[] = [
    // Primary: to the right
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
    // Fallback 1: to the left
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -8 },
    // Fallback 2: below
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    // Fallback 3: above
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
];

/**
 * Abstract base class shared by StoryBasicTextEditorComponent and StoryColaborationTextEditorComponent.
 *
 * Provides common inputs/outputs, signals, cursor save/restore, and the full production command
 * insert flow (toolbar buttons + keyboard shortcuts Ctrl+D/H/J/K/L).
 *
 * Concrete editors must implement `emitContentChange(html)` to route the updated HTML either
 * directly to the `contentChanged` output (basic) or through the debounce pipeline (collab).
 *
 * Concrete editors must also define their own `froalaOptions` computed signal and call
 * `applyProductionCommandListeners(editorInstance)` inside the Froala `initialized` event.
 */
@Directive()
export abstract class StoryTextEditorBase implements OnDestroy {
    protected readonly store = inject(Store);
    protected readonly dialog = inject(MatDialog);
    protected readonly ngZone = inject(NgZone);
    protected readonly modalService = inject(ModalService);
    private readonly mosEventService = inject(MosEventService);
    private readonly overlay = inject(Overlay);

    // Inputs
    readonly text = input<string>('');
    readonly disabled = input(false, { transform: coerceBooleanProperty });
    readonly showToolbar = input(true, { transform: coerceBooleanProperty });
    readonly storyGuid = input<string | null | undefined>(null);
    readonly approved = input(false, { transform: coerceBooleanProperty });

    // Outputs
    readonly contentChanged = output<string>();
    readonly save = output<void>();
    readonly selectedTextChange = output<string>();
    readonly mosItemOpen = output<MosEditorEvent>();
    readonly mosMediaClicked = output<string>();
    readonly approvedChanged = output<boolean>();

    // State
    public readonly editor = signal<FroalaEditor | null>(null);
    // storyGuid is included in the source even though the computation only uses text.
    // Angular signals skip propagation when the produced value is equal to the previous
    // one, so navigating between two stories with identical text (e.g. both empty) would
    // leave [froalaModel] unchanged and Froala would keep displaying the previous story's
    // unsaved edits. Including storyGuid ensures the source object changes on every story
    // navigation, forcing the computation to run regardless of text equality.
    // The story-change effect in the constructor handles the case where the signal chain
    // still does not reach Froala (e.g. if [froalaModel] itself also sees no change).
    readonly content = linkedSignal({
        source: () => ({ text: this.text(), storyGuid: this.storyGuid() }),
        computation: curr => curr.text ?? '',
    });

    // Computed
    protected readonly editorApiKey = computed(() => this.store.selectSignal(selectEditorApiKey)());
    protected readonly languageSettings = this.store.selectSignal(selectI18N);

    // Dialog state
    protected currentCommandDialogRef: MatDialogRef<ProductionCommandDialogComponent> | null = null;

    clearPreviousSelection = false;
    isDraggingExistingCommand = false;
    isDisabledArea = false;
    protected isProcessingContentChange = false;
    protected userHasInteracted = false;
    // Guards against a spurious contentChanged emission after a programmatic html.set()
    // during story navigation. The specific danger is the userHasInteracted path in the
    // basic editor's contentChanged handler: userHasInteracted is set to true on every
    // non-modifier keydown, so if the user was typing in Story A before navigating to
    // Story B, that flag is still true when the programmatic reset fires. The handler
    // would then call confirmApprovedStoryEdit, which on an approved Story B shows the
    // "confirm edit" modal — triggered by a reset the user never initiated. On a
    // non-approved story the emission still reaches the parent and marks Story B as dirty
    // before the user has typed anything.
    //
    // The collaboration editor needs no such guard: it does not register a Froala
    // contentChanged listener at all. Its content pipeline runs through Codox's hook,
    // which fires only for remote edits received via sync — a local html.set() never
    // enters that path, so the spurious event has nowhere to land.
    //
    // The flag is set before html.set() and consumed (cleared) inside the basic editor's
    // contentChanged handler — NOT reset immediately after html.set(). Froala debounces
    // contentChanged internally (~100 ms), so resetting the flag right after html.set()
    // would always win that race and leave the handler without the suppression signal.
    protected programmaticResetPending = false;
    private selectedTextSubject = new Subject<string>();
    protected readonly destroy$ = new Subject<void>();

    /**
     * Returns the toolbar / HTML sanitization config shared by both editors, including
     * the paste event handlers (`paste.before` and `paste.beforeCleanup`).
     * The concrete `froalaOptions` computed spreads this, adds `key`, and spreads
     * `commonFroalaConfig.events` into its own `events` object alongside `initialized`.
     */
    protected get commonFroalaConfig() {
        return {
            toolbarSticky: false,
            tooltips: false,
            enter: FroalaEditor.ENTER_BR,
            pluginsEnabled: ['link', 'url', 'paragraphFormat', EditorActions.SAVE],
            htmlAllowedEmptyTags: HTML_ALLOWED_EMPTY_TAGS,
            htmlAllowedAttrs: htmlAllowedAttrs,
            toolbarButtons: {
                moreText: {
                    buttons: [EditorActions.SAVE, 'bold', 'italic', 'underline', 'strikeThrough', 'paragraphFormat', 'undo', 'redo', 'insertLink'],
                    buttonsVisible: 8,
                },
                moreProductionCommands: {
                    buttons: [...PRODUCTION_COMMAND_BUTTONS],
                    align: 'left' as const,
                    buttonsVisible: 6,
                },
            },

            events: {
                // paste.before — handles the specific case where a user pastes raw HTML text
                // (e.g. '<a href="https://ap.org">AP</a>' copied from a text editor or internal
                // tool). The plain-text clipboard contains the literal HTML string; without this
                // handler Froala would insert it as visible text rather than a rendered anchor.
                // Returns false to skip further paste processing for this case only.
                'paste.before': (clipboardEvent: ClipboardEvent) => {
                    const pastedText = clipboardEvent.clipboardData?.getData('Text')?.trim();
                    if (pastedText && /<a.*>.*<\/a>/gi.test(pastedText)) {
                        this.editor()?.html.insert(pastedText);
                        return false;
                    }
                },
                // paste.beforeCleanup — strip complex markup (Word styles, nested divs, etc.) down
                // to only bold/italic/underline/anchor/br before Froala applies its own cleanup.
                // Anchor tags are preserved with their href by convertHtmlToSimplifiedHtml.
                'paste.beforeCleanup': (clipboardHtml: string) => {
                    return convertHtmlToSimplifiedHtml(clipboardHtml);
                },
            },
        };
    }

    /**
     * How the editor publishes updated HTML after a production command is inserted.
     * Basic editor: emit directly. Collaboration editor: push through debounce pipeline.
     */
    protected abstract emitContentChange(html: string): void;

    /**
     * Whether the editor should currently be read-only. Defaults to the `disabled` input.
     * The collaboration editor overrides this to also account for Codox initialisation state.
     */
    protected isEditorEffectivelyDisabled(): boolean {
        return this.disabled();
    }

    /**
     * Attaches the `productionCommand` Froala event and the Ctrl+D/H/J/K/L keyboard shortcuts
     * to an already-initialised Froala editor instance. Call this inside `froalaOptions.events.initialized`.
     */
    protected applyCommonListeners(editorInstance: FroalaEditor): void {
        editorInstance.events.on(
            'productionCommand',
            (title: ProductionCommandType) => {
                this.ngZone.run(() => {
                    this.productionCommandInsertHandler(title.toUpperCase() as ProductionCommandType);
                });
            },
            true
        );

        editorInstance.events.on(
            'keydown',
            (e: KeyboardEvent) => {
                // Track that the next content change was user-initiated (not programmatic).
                // Only set for plain character/editing keys — not Ctrl/Alt/Meta shortcuts.
                if (!e.ctrlKey && !e.altKey && !e.metaKey) {
                    this.userHasInteracted = true;
                }

                if (e.ctrlKey && !e.shiftKey && !e.altKey) {
                    switch (e.keyCode) {
                        case KeyCodes.S:
                            e.preventDefault();
                            this.save.emit();
                            return false;
                        case KeyCodes.D:
                            e.preventDefault();
                            this.ngZone.run(() => this.productionCommandInsertHandler(ProductionCommandType.DURATION));
                            return false;
                        case KeyCodes.H:
                            e.preventDefault();
                            this.ngZone.run(() => this.productionCommandInsertHandler(ProductionCommandType.NOTES));
                            return false;
                        case KeyCodes.J:
                            e.preventDefault();
                            this.ngZone.run(() => this.productionCommandInsertHandler(ProductionCommandType.INSERT));
                            return false;
                        case KeyCodes.K:
                            e.preventDefault();
                            this.ngZone.run(() => this.productionCommandInsertHandler(ProductionCommandType.TAKE));
                            return false;
                        case KeyCodes.L:
                            e.preventDefault();
                            this.ngZone.run(() => this.productionCommandInsertHandler(ProductionCommandType.ANCHOR));
                            return false;
                        case KeyCodes.SEMICOLON:
                            e.preventDefault();
                            this.insertDateTime(editorInstance);
                            return false;
                    }
                } else if (e.ctrlKey && e.shiftKey && !e.altKey) {
                    switch (e.keyCode) {
                        case KeyCodes.SEMICOLON:
                            e.preventDefault();
                            this.insertTime(editorInstance);
                            return false;
                    }
                }
            },
            true
        );

        editorInstance.events.on(
            EditorActions.SAVE,
            () => {
                this.save.emit();
            },
            true
        );

        // Handle clicks on production command and MOS element action buttons.
        // Native addEventListener is used here because Froala's own click event
        // does not bubble from inside contenteditable=false elements in all browsers.
        editorInstance.events.on(
            'click',
            (e: MouseEvent) => {
                const target = e.target as Element;
                if (!target) return;

                if (target.classList.contains('st-rich-text-editor--production-close')) {
                    e.stopPropagation();
                    const mosParent = target.closest('.st-rich-text-editor--mos');
                    const prodParent = target.closest('.st-rich-text-editor--production');
                    const elementId = mosParent?.id || prodParent?.id;
                    if (elementId) {
                        this.ngZone.run(() => this.confirmThenRemoveElement(elementId, editorInstance));
                    }
                    return false;
                }

                if (target.classList.contains('st-rich-text-editor--settings')) {
                    e.stopPropagation();
                    const prodParent = target.closest('.st-rich-text-editor--production');
                    if (prodParent?.id) {
                        this.ngZone.run(() => this.openProductionCommandEdit(prodParent.id, editorInstance));
                    }
                    return false;
                }

                if (target.classList.contains('st-rich-text-editor--mos-open-external')) {
                    e.stopPropagation();
                    const mosParentElement = target.closest('.st-rich-text-editor--mos');
                    const mosPluginIdElement = mosParentElement?.querySelector('.st-rich-text-editor--mos--plugin-id');
                    if (mosPluginIdElement) {
                        const mosPluginId = mosPluginIdElement.id;
                        this.ngZone.run(() =>
                            this.openSidecarChooser(target, {
                                mosId: mosParentElement.id,
                                mosPluginId,
                                storyGuid: this.storyGuid(),
                            })
                        );
                    }
                    return false;
                }

                if (target.classList.contains('st-rich-text-editor--mos-media')) {
                    e.stopPropagation();
                    const mosElement = target.closest('.st-rich-text-editor--mos');
                    if (mosElement?.id) {
                        this.ngZone.run(() => this.mosMediaClicked.emit(mosElement.id));
                    }
                    return false;
                }

                const productionCommandExpand = target.closest('.st-rich-text-editor--production-expander');
                if (productionCommandExpand) {
                    e.stopPropagation();
                    const expanderIcon = productionCommandExpand.textContent;
                    productionCommandExpand.textContent = expanderIcon === 'expand_more' ? 'expand_less' : 'expand_more';
                    const mosCommandParent = productionCommandExpand.closest('.st-rich-text-editor--mos');
                    const prodCommandParent = productionCommandExpand.closest('.st-rich-text-editor--production');
                    if (mosCommandParent) {
                        this.toggleDetails(mosCommandParent, 'st-rich-text-editor--mos-details');
                    } else if (prodCommandParent) {
                        this.toggleDetails(prodCommandParent, 'st-rich-text-editor--production-details');
                        this.toggleDetails(prodCommandParent, 'st-rich-text-editor--production-details--notes');
                    }
                    return false;
                }
            },
            true
        );

        // Keep the MOS service informed of the nearest MIR element to the cursor.
        // Called on focus, blur, and mouseup so the sidecar always reflects the active MIR.
        const updateMirStatus = () =>
            this.mosEventService.setCurrentMirStatus({
                ...this.resolveCurrentMirItemId(editorInstance),
                storyId: this.storyGuid(),
            });

        // editorInstance.events.on('focus', updateMirStatus, true);
        editorInstance.events.on(
            'blur',
            _ => {
                this.ngZone.run(() => {
                    this.selectedTextChange.emit('');
                    this.clearPreviousSelection = false;
                });
            },
            true
        );
        editorInstance.events.on('mouseup', updateMirStatus, true);

        editorInstance.events.on(
            'mouseup',
            () => {
                const selectedText = editorInstance.selection.text().trim();
                if (selectedText) {
                    let prodCommand = false;
                    const selTextTest = window
                        .getSelection()
                        .toString()
                        .split('\n')
                        .reduce((acc, el) => {
                            if (el === '[PROD]') {
                                prodCommand = true;
                                return acc;
                            }
                            if (el === '[/PROD]') {
                                prodCommand = false;
                                return acc;
                            }
                            if (!prodCommand) {
                                acc.push(el);
                            }
                            return acc;
                        }, [])
                        .join('\n');
                    this.ngZone.run(() => {
                        this.selectedTextSubject.next(selTextTest);
                        this.clearPreviousSelection = true;
                    });
                }
                // this.mosEventService.setCurrentMirStatus(this.resolveCurrentMirId(), this.story()?.Guid);
            },
            true
        );
        editorInstance.events.on(
            'mousedown',
            () => {
                if (this.clearPreviousSelection) {
                    editorInstance.selection.clear();
                    this.ngZone.run(() => {
                        this.selectedTextSubject.next(editorInstance.selection.text().trim());
                        this.clearPreviousSelection = false;
                    });
                }
            },
            true
        );
        editorInstance.events.on('dragover', ev => {
            if (!this.isDraggingExistingCommand && !this.isDisabledArea) {
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
        });

        // On dragenter: check if the drop target is inside a non-editable area
        // (contenteditable=false) so we can block the drop cursor change.
        editorInstance.events.on('dragenter', ev => {
            const dragingStory = ev.originalEvent.dataTransfer.types.includes(DragEventKeys.DRAG_STORY);

            if (dragingStory) {
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
            this.isDisabledArea = false;
            if (!this.isDraggingExistingCommand) {
                editorInstance.markers.insertAtPoint(ev.originalEvent);
                const currentNode = editorInstance.selection.element();
                this.isDisabledArea = !!currentNode.closest('[contenteditable=false]');
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
        });

        editorInstance.events.on(
            'drop',
            ev => {
                let data = ev.originalEvent.dataTransfer.getData('text/plain');
                if (typeof data === 'string') {
                    try {
                        data = JSON.parse(data);
                    } catch (e) {
                        console.warn(e);
                    }
                }

                editorInstance.markers.insertAtPoint(ev.originalEvent);
                const $marker = editorInstance.$el.find('.fr-marker');
                $marker.replaceWith(FroalaEditor.MARKERS);
                editorInstance.selection.restore();
                if (!editorInstance.undo.canDo()) editorInstance.undo.saveStep();

                this.isDisabledArea = false;
                this.isDraggingExistingCommand = false;
                ev.preventDefault();
                ev.stopPropagation();

                this.confirmApprovedStoryEdit(() => {
                    if (data?.type === DRAGTYPES.AttachmentPool || data?.type === DRAGTYPES.MosPluginDragEnd || data?.type === DRAGTYPES.MosPluginDragStart) {
                        const currentNode = editorInstance.selection.element();
                        if (!currentNode.closest('[contenteditable=false]')) {
                            // Derive a stable ID from the MOS <objID> so all collaborators assign
                            // the same key to the same media item, matching the addMosItem() approach.
                            const mosCommand = data.data;
                            const parsedMosXml = parseXml(mosCommand);
                            const objId = parsedMosXml?.getElementsByTagName('objID')[0]?.textContent?.trim() || parsedMosXml?.getElementsByTagName('objid')[0]?.textContent?.trim() || null;
                            const newElId = objId ? generateItemIDbyObjId(objId) : v4();
                            const mosItemElement = ML2HtmlUtils.generateMosItemHtml(mosCommand, newElId);
                            if (mosItemElement) {
                                editorInstance.html.insert(mosItemElement.outerHTML);
                            }
                        }
                    } else {
                        const element = document.getElementById(data);
                        if (element) {
                            editorInstance.html.insert(element.outerHTML);
                            element.remove();
                        }
                    }
                    editorInstance.undo.saveStep();
                });

                return false;
            },
            true
        );
    }

    /**
     * Returns the ID of the MOS/MIR element closest to the current caret position
     * inside `editorInstance`. Returns `null` when there are no MIR elements.
     *
     * Resolution order:
     *  1. First MIR that appears *after* the caret in document order.
     *  2. Last MIR that appears *before* the caret.
     *  3. First MIR in the editor (fallback when there is no selection).
     */
    public resolveCurrentMirItemId(editorInstance: FroalaEditor): {
        mirItemId: string | null;
        mosCommand?: string;
    } {
        if (!editorInstance?.el) return null;

        // const allMirs = Array.from(editorInstance.el.getElementsByClassName('st-rich-text-editor--mos')) as HTMLElement[];
        const allMirs = Array.from(editorInstance.el.querySelectorAll('.st-rich-text-editor--mos--item-id')) as HTMLElement[];
        if (!allMirs.length) return null;

        // We rely on the fact that the MIR item ID is stored in a sibling element with a known class,
        // so that we can return the corresponding MOS command along with the MIR item ID when we resolve the current MIR.
        // This allows us to keep track of the current MOS command in the MOS service without having to re-query the editor for the command when we need to send it to the plugin.
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) {
            const mirItemIdEl = allMirs[0] || null;
            if (mirItemIdEl) {
                const rawMosCommand = decodeURIComponent(mirItemIdEl.nextElementSibling?.textContent || '');
                return { mirItemId: mirItemIdEl.id, mosCommand: rawMosCommand };
            }
            return null;
        }

        const cursorNode = selection.getRangeAt(0).startContainer;

        const mirAfterCursor = allMirs.find(mir => !!(cursorNode.compareDocumentPosition(mir) & Node.DOCUMENT_POSITION_FOLLOWING));
        if (mirAfterCursor?.id) {
            return { mirItemId: mirAfterCursor.id, mosCommand: decodeURIComponent(mirAfterCursor.nextElementSibling?.textContent || '') };
        }

        const lastMirBeforeCursor = [...allMirs].reverse().find(mir => !!(cursorNode.compareDocumentPosition(mir) & Node.DOCUMENT_POSITION_PRECEDING));
        if (lastMirBeforeCursor?.id) {
            return { mirItemId: lastMirBeforeCursor.id, mosCommand: decodeURIComponent(lastMirBeforeCursor.nextElementSibling?.textContent || '') };
        }
        const firstMir = allMirs[0];
        if (firstMir?.id) {
            return { mirItemId: firstMir.id, mosCommand: decodeURIComponent(firstMir.nextElementSibling?.textContent || '') };
        }
        return null;
    }

    constructor() {
        // Enable/disable editor reactively. `isEditorEffectivelyDisabled` may read additional
        // signals (e.g. codoxAvailable) in the collab editor — those are tracked automatically.
        effect(() => {
            const editorInstance = this.editor();
            if (!editorInstance) return;

            if (this.isEditorEffectivelyDisabled()) {
                editorInstance.edit.off();
            } else {
                editorInstance.edit.on();
            }
        });

        // When the story identity changes, Angular's signal equality check may suppress
        // propagation through the chain if the new story has identical text to the previous
        // one (e.g. both empty). In that case [froalaModel] never receives a new push and
        // Froala keeps displaying the previous story's unsaved edits.
        //
        // This effect bypasses that by directly comparing what Froala is *showing* against
        // what it *should* show, and calling html.set() only when they diverge.
        //
        // editor() and text() are read inside untracked() so they do NOT become effect
        // dependencies. If they were tracked, any RTU or store update for the same story
        // would re-run the effect and call html.set() mid-edit, resetting Froala and
        // jumping the cursor. storyGuid is the only intended trigger — it changes exactly
        // when the user navigates to a different story.
        //
        // See programmaticResetPending above for why the flag is not cleared here.
        effect(() => {
            const guid = this.storyGuid();
            if (!guid) return;

            untracked(() => {
                const ed = this.editor();
                if (!ed) return;
                const expected = this.text() ?? '';
                if (ed.html.get() !== expected) {
                    this.programmaticResetPending = true;
                    ed.html.set(expected);
                }
            });
        });

        this.selectedTextSubject
            .pipe(
                throttle(
                    () => {
                        const throttleInterval = environment.read_rate_time_throttle_interval_seconds ? environment.read_rate_time_throttle_interval_seconds * 1000 : 1000;
                        return interval(throttleInterval);
                    },
                    {
                        leading: true,
                        trailing: true,
                    }
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(selectedText => {
                this.selectedTextChange.emit(selectedText);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ─── MOS item insert / replace ──────────────────────────────────────────────
    // Called by StoryEditorComponent when MosEventService emits an add or edit event
    // that matches this story's Guid. Each method operates on the current Froala instance
    // (whichever editor the user last focused — above or below).

    /**
     * Inserts a new MOS item at the current caret position in the Froala editor.
     * Uses the same temporary-marker technique as production command insertion to
     * capture the caret offset before the editor loses focus.
     */
    addMosItem(mosCommand: string): void {
        const ed = this.editor();
        if (!ed) return;

        this.confirmApprovedStoryEdit(() => {
            // Derive a deterministic ID from the MOS <objID> so all users/sessions
            // assign the same key to the same media item (fixes collaboration UUID mismatch).
            const newElId = v4();
            const mosItemElement = ML2HtmlUtils.generateMosItemHtml(mosCommand, newElId);
            if (!mosItemElement) {
                console.warn('StoryTextEditor: Failed to generate MOS item HTML');
                return;
            }

            if (!ed.undo.canDo()) ed.undo.saveStep();
            let uniqueMarker = '<span id="unique-caret-marker"></span>';
            ed.html.insert(uniqueMarker);
            let content = ed.html.get();

            const isMarkerInSpan = content.includes('<span>' + uniqueMarker + '</span>');
            uniqueMarker = isMarkerInSpan ? '<span>' + uniqueMarker + '</span>' : uniqueMarker;
            let caretPosition = content.indexOf(uniqueMarker);
            if (caretPosition < 0) caretPosition = 0;

            content = content.replace(uniqueMarker, '');
            ed.html.set(content);

            // Skip past the [/MOS] closing marker (and its wrapper </div></div>) if the caret sits
            // inside it, so the new item appears after rather than inside the previous block.
            caretPosition = resolvePointerPastClosedCommand(content, caretPosition, '[/MOS]');

            const partBefore = content.substring(0, caretPosition);
            const partAfter = content.substring(caretPosition);
            const parentTag = findParentTag(partBefore);

            const updatedContent = parentTag ? `${partBefore}</${parentTag}>${mosItemElement.outerHTML}<${parentTag}>${partAfter}` : `${partBefore}${mosItemElement.outerHTML}${partAfter}`;

            const parsed = parseXml(updatedContent, 'text/html');
            ed.html.set(parsed.body.innerHTML);

            const insertedEl = ed.el.querySelector(`[id="${newElId}"]`);
            if (insertedEl) {
                ed.selection.setAfter(insertedEl);
                ed.selection.restore();
            } else {
                ed.el.focus();
            }

            this.emitContentChange(ed.html.get());
        });
    }

    /**
     * Replaces the MOS element identified by `mosElId` in the Froala editor with
     * updated content from `mosCommand`. The element ID is stable across edits —
     * it was assigned by `addMosItem` when the item was first inserted.
     */
    editMosItem(mosCommand: string, mosElId: string): void {
        const ed = this.editor();
        if (!ed) return;

        this.confirmApprovedStoryEdit(() => {
            const mosItemElement = ML2HtmlUtils.generateMosItemHtml(mosCommand, mosElId);
            if (!mosItemElement) {
                console.warn(`StoryTextEditor: Failed to generate replacement MOS item HTML for element "${mosElId}"`);
                return;
            }

            const parsed = parseXml(ed.html.get(), 'text/html');
            const allMirs = Array.from(ed.el.querySelectorAll('.st-rich-text-editor--mos--item-id')) as HTMLElement[];
            // find the matching MIR element that has the same itemId, its parent element is the one we want to replace and has id the textCommandId so we can find it in the parsed document and replace it with the new content, if we try to find the element with the mosElId directly in the parsed document it won't work because the id is on a child element not the one we want to replace
            const matchingMirId = allMirs.find(el => el.id === mosElId)?.parentElement?.id;

            const existingEl = parsed.getElementById(matchingMirId);
            if (existingEl) {
                existingEl.replaceWith(mosItemElement);
                ed.html.set(parsed.body.innerHTML);
                this.emitContentChange(ed.html.get());
            } else {
                console.warn(`StoryTextEditor: MOS element "${mosElId}" not found in editor — cannot replace`);
            }
        });
    }

    // ─── Production command / MOS element actions ───────────────────────────────
    // These private methods are triggered by the click handler registered in
    // applyCommonListeners. They mirror the equivalent logic in st-rich-text-editor,
    // adapted for the simpler story-editor context (no TextCommands dictionary —
    // raw content is read directly from the hidden DOM content elements).

    /** Ctrl+; — inserts the current date and time at the caret position. */
    private insertDateTime(ed: FroalaEditor): void {
        if (!ed.undo.canDo()) ed.undo.saveStep();
        ed.html.insert(DateTime.now().toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS));
        ed.undo.saveStep();
    }

    /** Ctrl+Shift+; — inserts the current time at the caret position. */
    private insertTime(ed: FroalaEditor): void {
        if (!ed.undo.canDo()) ed.undo.saveStep();
        ed.html.insert(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS));
        ed.undo.saveStep();
    }

    /**
     * Opens a confirmation dialog and, if the user confirms, removes the element
     * identified by `elementId` from the editor DOM.
     */
    private confirmThenRemoveElement(elementId: string, ed: FroalaEditor): void {
        this.confirmApprovedStoryEdit(() => {
            const ls = this.languageSettings();
            this.modalService
                .openModal({
                    title: `${ls.PSTWebConfirmDeletePart1} item?`,
                    content: '',
                    yesString: ls.PSTWebOkButtonLabel,
                    noString: ls.PSTWebCancelButtonLabel,
                })
                .result.pipe(
                    take(1),
                    catchError(() => {
                        return [ModalResponseCodes.CANCEL];
                    })
                )
                .subscribe(res => {
                    if (res === ModalResponseCodes.OK) {
                        this.removeElements([elementId], ed);
                    }
                });
        });
    }

    /**
     * Removes the production command or MOS elements identified by `elementIds`
     * from the editor DOM and emits the updated content.
     */
    private removeElements(elementIds: string[], ed: FroalaEditor): void {
        for (const id of elementIds) {
            const element = ed.el.querySelector(`#${CSS.escape(id)}`);
            if (element) {
                // Remove a trailing empty <p> sibling if present, to avoid leaving
                // a blank line after the deleted block (mirrors st-rich-text-editor).
                const next = element.nextElementSibling;
                if (next && next.tagName.toLowerCase() === 'p' && !next.textContent?.trim()) {
                    next.remove();
                }
                element.remove();
            }
        }
        this.emitContentChange(ed.html.get());
    }

    /**
     * Reads the raw production command from the hidden content `<div>` embedded in
     * the element, parses it into a `ProductionCommand`, and opens the edit dialog.
     */
    private openProductionCommandEdit(elementId: string, ed: FroalaEditor): void {
        // The hidden content element stores the command text with \u000a (PRODUCTION_COMMAND_ROW_SEPARATOR)
        // as the row separator. parseProductionCommand handles \r?\n, so textContent works directly.
        const contentEl = ed.el.querySelector(`#${CSS.escape(PRODUCTION_CONTENT_ID_PREFIX + elementId)}`);
        if (!contentEl?.textContent) return;

        let parsedCommand: ProductionCommand | null = null;
        try {
            parsedCommand = parseProductionCommand(`[${contentEl.textContent}]`, elementId);
        } catch (error) {
            console.warn('StoryTextEditor: Failed to parse production command for editing', error);
            return;
        }
        if (!parsedCommand) return;

        this.confirmApprovedStoryEdit(() => {
            if (parsedCommand.commandType === ProductionCommandType.ANCHOR || parsedCommand.commandType === ProductionCommandType.READRATE) {
                this.handleAnchorReadRateEdit(parsedCommand, elementId, ed);
            } else {
                this.openEditDialog(parsedCommand, [elementId], ed);
            }
        });
    }

    /**
     * For ANCHOR or READRATE commands, looks for the adjacent paired element in the
     * editor DOM, reads its content if present, and opens the edit dialog with both
     * commands (Anchor + ReadRate) together — the same behaviour as the insert dialog.
     */
    private handleAnchorReadRateEdit(parsedCommand: ProductionCommand, elementId: string, ed: FroalaEditor): void {
        const currentEl = ed.el.querySelector(`#${CSS.escape(elementId)}`);
        if (!currentEl) return;

        // ANCHOR looks ahead for READRATE; READRATE looks back for ANCHOR.
        const siblingEl = parsedCommand.commandType === ProductionCommandType.ANCHOR ? currentEl.nextElementSibling : currentEl.previousElementSibling;

        let adjacentCommand: ProductionCommand | null = null;
        if (siblingEl?.classList.contains('st-rich-text-editor--production') && siblingEl.id) {
            const adjacentContentEl = ed.el.querySelector(`#${CSS.escape(PRODUCTION_CONTENT_ID_PREFIX + siblingEl.id)}`);
            if (adjacentContentEl?.textContent) {
                try {
                    const parsed = parseProductionCommand(`[${adjacentContentEl.textContent}]`, siblingEl.id);
                    if (
                        (parsedCommand.commandType === ProductionCommandType.ANCHOR && parsed.commandType === ProductionCommandType.READRATE) ||
                        (parsedCommand.commandType === ProductionCommandType.READRATE && parsed.commandType === ProductionCommandType.ANCHOR)
                    ) {
                        adjacentCommand = parsed;
                    }
                } catch {
                    // If parsing fails, proceed without adjacent command.
                }
            }
        }

        const anchorCommand = parsedCommand.commandType === ProductionCommandType.ANCHOR ? parsedCommand : adjacentCommand;
        const readRateCommand = parsedCommand.commandType === ProductionCommandType.READRATE ? parsedCommand : adjacentCommand;

        // Build the pair for the dialog; use '' guid when a slot has no existing element.
        const commands: ProductionCommand[] = [
            {
                commandType: ProductionCommandType.ANCHOR,
                guid: anchorCommand?.[ProductionFieldName.TEXT] ? anchorCommand.guid || v4() : '',
                [ProductionFieldName.TEXT]: anchorCommand?.[ProductionFieldName.TEXT] || '',
            } as AnchorCommand,
            {
                commandType: ProductionCommandType.READRATE,
                guid: readRateCommand?.[ProductionFieldName.TEXT] ? readRateCommand.guid || v4() : '',
                [ProductionFieldName.TEXT]: readRateCommand?.[ProductionFieldName.TEXT] || '',
            } as ReadRateCommand,
        ];

        const elementIds = [elementId, ...(adjacentCommand?.guid ? [adjacentCommand.guid] : [])];
        this.openEditDialog(commands, elementIds, ed);
    }

    /**
     * Opens `ProductionCommandDialogComponent` in edit mode and wires the
     * `onUpdate` and `onDelete` subjects to the DOM-manipulation helpers.
     */
    private openEditDialog(commands: ProductionCommand | ProductionCommand[], elementIds: string[], ed: FroalaEditor): void {
        if (this.currentCommandDialogRef?.getState() === MatDialogState.OPEN) {
            this.currentCommandDialogRef.close();
        }

        if (Array.isArray(commands)) {
            this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                hasBackdrop: true,
                panelClass: 'st-production-command-dialog',
                data: { commands, commandType: commands[0].commandType, editMode: true },
                autoFocus: true,
                delayFocusTrap: true,
            });
        } else {
            this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                hasBackdrop: true,
                panelClass: 'st-production-command-dialog',
                data: { command: commands, commandType: commands.commandType, editMode: true },
                autoFocus: true,
                delayFocusTrap: true,
            });
        }

        const destroyed$ = this.currentCommandDialogRef.componentInstance.destroyed$;

        (this.currentCommandDialogRef.componentInstance.onUpdate as unknown as Observable<ProductionCommand | ProductionCommand[]>)
            .pipe(
                tap(updateCommand => this.applyProductionCommandUpdate(updateCommand, ed)),
                takeUntil(destroyed$)
            )
            .subscribe();

        this.currentCommandDialogRef.componentInstance.onDelete
            .pipe(
                tap(() => this.removeElements(elementIds, ed)),
                takeUntil(destroyed$)
            )
            .subscribe();
    }

    /**
     * Applies a production command update (single or ANCHOR/READRATE pair) to the
     * live editor DOM, then emits the updated content.
     *
     * For each command:
     *  - If the serialised text is non-empty, regenerate its HTML and replace the
     *    existing element in the DOM (or insert adjacent to its partner when new).
     *  - If the serialised text is empty, remove the existing element.
     */
    private applyProductionCommandUpdate(updateCommand: ProductionCommand | ProductionCommand[], ed: FroalaEditor): void {
        if (Array.isArray(updateCommand)) {
            // ANCHOR/READRATE pair — handle insertion / replacement / removal together.
            const [anchorCmd, readRateCmd] = updateCommand as [AnchorCommand, ReadRateCommand];

            // Locate existing DOM elements before any mutation.
            let anchorEl = anchorCmd.guid ? ed.el.querySelector(`#${CSS.escape(anchorCmd.guid)}`) : null;
            let readRateEl = readRateCmd.guid ? ed.el.querySelector(`#${CSS.escape(readRateCmd.guid)}`) : null;

            for (const cmd of [anchorCmd, readRateCmd]) {
                if (!cmd.guid) cmd.guid = v4();

                const textProdCommand = this.convertProductionCommandToText(cmd);
                const existingEl = cmd.commandType === ProductionCommandType.ANCHOR ? anchorEl : readRateEl;

                if (!textProdCommand) {
                    // Empty → remove existing element (if any).
                    existingEl?.remove();
                    continue;
                }

                const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), cmd.guid);
                if (!productionItemElement) continue;

                if (existingEl) {
                    existingEl.replaceWith(productionItemElement);
                    if (cmd.commandType === ProductionCommandType.ANCHOR) {
                        anchorEl = productionItemElement;
                    } else {
                        readRateEl = productionItemElement;
                    }
                } else {
                    // New element — insert adjacent to its partner.
                    if (cmd.commandType === ProductionCommandType.ANCHOR && readRateEl) {
                        readRateEl.parentElement?.insertBefore(productionItemElement, readRateEl);
                        anchorEl = productionItemElement;
                    } else if (cmd.commandType === ProductionCommandType.READRATE && anchorEl) {
                        anchorEl.after(productionItemElement);
                        readRateEl = productionItemElement;
                    }
                }
            }
        } else {
            // Single command update.
            const cmd = updateCommand;
            if (!cmd.guid) cmd.guid = v4();

            const textProdCommand = this.convertProductionCommandToText(cmd);
            const existingEl = ed.el.querySelector(`#${CSS.escape(cmd.guid)}`);

            if (!textProdCommand) {
                existingEl?.remove();
            } else {
                const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), cmd.guid);
                if (productionItemElement && existingEl) {
                    existingEl.replaceWith(productionItemElement);
                }
            }
        }

        // Re-render through Froala so the replaced element's CSS styles are applied.
        // Direct DOM replaceWith() bypasses Froala's render pipeline; html.set() restores it.
        // programmaticResetPending suppresses the spurious contentChanged that html.set() fires.
        this.programmaticResetPending = true;
        ed.html.set(ed.html.get());

        // html.set() discards the cursor; re-query by GUID (old DOM refs are stale after the
        // re-render) and place the caret after the last updated element, mirroring the insert paths.
        let cursorEl: Element | null = null;
        if (Array.isArray(updateCommand)) {
            const [anchorCmd, readRateCmd] = updateCommand as [AnchorCommand, ReadRateCommand];
            cursorEl = readRateCmd.guid ? ed.el.querySelector(`#${CSS.escape(readRateCmd.guid)}`) : null;
            if (!cursorEl && anchorCmd.guid) {
                cursorEl = ed.el.querySelector(`#${CSS.escape(anchorCmd.guid)}`);
            }
        } else {
            const cmd = updateCommand as ProductionCommand;
            cursorEl = cmd.guid ? ed.el.querySelector(`#${CSS.escape(cmd.guid)}`) : null;
        }

        if (cursorEl) {
            ed.selection.setAfter(cursorEl);
            ed.selection.restore();
        } else {
            ed.el.focus();
        }

        this.emitContentChange(ed.html.get());
    }

    // ─── Production Commands ────────────────────────────────────────────────────
    // Production command buttons are registered globally in i18n.effects.ts via
    // FroalaEditor.RegisterCommand. Each button fires `this.events.trigger('productionCommand',
    // [commandName])`, which `applyProductionCommandListeners` listens for. Keyboard shortcuts
    // Ctrl+D/H/J/K/L map to the same handler below.

    protected productionCommandInsertHandler(commandType: ProductionCommandType): void {
        const ed = this.editor();
        if (!ed) return;

        // Insert a temporary marker span at the current caret position so we can recover the
        // insertion offset after the dialog is opened and the editor loses focus.
        // We must do this before opening the dialog because `ed.selection.save()` relies on
        // Froala marker spans that get discarded when the editor is blurred.
        if (!ed.undo.canDo()) ed.undo.saveStep();
        let uniqueMarker = '<span id="unique-caret-marker"></span>';
        ed.html.insert(uniqueMarker);
        let content = ed.html.get();

        // Froala may wrap an inline marker in a parent <span>; account for both forms.
        const isMarkerInSpan = content.includes('<span>' + uniqueMarker + '</span>');
        uniqueMarker = isMarkerInSpan ? '<span>' + uniqueMarker + '</span>' : uniqueMarker;
        let caretPosition = content.indexOf(uniqueMarker);
        if (caretPosition < 0) caretPosition = 0;

        // Strip the marker from the HTML before setting it back so it doesn't appear in the output.
        content = content.replace(uniqueMarker, '');
        ed.html.set(content);

        const productionCommand = this.initCommand(commandType);
        if (!productionCommand) return;

        this.confirmApprovedStoryEdit(() => {
            // Close any already-open dialog before opening a new one to avoid stacking.
            if (this.currentCommandDialogRef?.getState() === MatDialogState.OPEN) {
                this.currentCommandDialogRef.close();
            }

            // ANCHOR opens a dialog carrying two commands (Anchor + ReadRate) and is handled
            // separately because the dialog emits an array rather than a single command.
            if (Array.isArray(productionCommand)) {
                this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                    hasBackdrop: true,
                    panelClass: 'st-production-command-dialog',
                    data: { commands: productionCommand },
                    autoFocus: true,
                    delayFocusTrap: true,
                });
                this.handleAnchorInsert(caretPosition);
            } else {
                this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
                    hasBackdrop: true,
                    panelClass: 'st-production-command-dialog',
                    data: { command: productionCommand },
                    autoFocus: true,
                    delayFocusTrap: true,
                });
                this.handleSingleCommandInsert(caretPosition);
            }
        });
    }

    private handleSingleCommandInsert(caretPosition: number): void {
        (this.currentCommandDialogRef.componentInstance.onInsert as unknown as Observable<ProductionCommand>)
            .pipe(
                tap((command: ProductionCommand) => {
                    if (!command) return;
                    const textProdCommand = this.convertProductionCommandToText(command);
                    if (!textProdCommand) return;

                    const newElId = v4();
                    const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), newElId);
                    if (!productionItemElement) {
                        console.warn('StoryTextEditor: Failed to generate production command HTML');
                        return;
                    }

                    const ed = this.editor();
                    if (!ed) return;
                    const content = ed.html.get();
                    let insertPos = caretPosition;

                    // If the caret sits inside a closing production command block's wrapper tags
                    // ([/PROD]</div></div>), skip past it so the new command follows it cleanly
                    // rather than being injected inside the previous one.
                    insertPos = resolvePointerPastClosedCommand(content, insertPos, '[/PROD]');

                    const partBefore = content.substring(0, insertPos);
                    const partAfter = content.substring(insertPos);
                    const parentTag = findParentTag(partBefore);

                    // If the caret is inside an inline tag (e.g. <b>, <i>), close and reopen it
                    // around the inserted element so the command doesn't inherit inline styles.
                    const updatedContent = parentTag
                        ? `${partBefore}</${parentTag}>${productionItemElement.outerHTML}<${parentTag}>${partAfter}`
                        : `${partBefore}${productionItemElement.outerHTML}${partAfter}`;

                    // Run through the browser's HTML parser to normalise the markup before
                    // setting it back into the editor.
                    const parsed = parseXml(updatedContent, 'text/html');
                    ed.html.set(parsed.body.innerHTML);

                    // Restore focus and place the caret immediately after the inserted element.
                    // The generated <div> has id="${newElId}", so we can locate it in the editor
                    // DOM. Froala's setAfter/restore positions the caret without triggering a
                    // full HTML reset (which would move the cursor to position 0).
                    const insertedEl = ed.el.querySelector(`[id="${newElId}"]`);
                    if (insertedEl) {
                        ed.selection.setAfter(insertedEl);
                        ed.selection.restore();
                    } else {
                        ed.el.focus();
                    }

                    this.emitContentChange(ed.html.get());
                })
            )
            .subscribe();
    }

    private handleAnchorInsert(caretPosition: number): void {
        (this.currentCommandDialogRef.componentInstance.onInsert as unknown as Observable<ProductionCommand[]>)
            .pipe(
                tap((commands: ProductionCommand[]) => {
                    if (!commands) return;
                    const ed = this.editor();
                    if (!ed) return;

                    let content = ed.html.get();
                    // Track the running insertion offset so each subsequent command is appended
                    // immediately after the previous one rather than all being inserted at the
                    // original caret position. Also guard against the caret landing inside a <div>
                    let lastPointerPosition = resolvePointerPastClosedCommand(content, caretPosition, '[/PROD]');
                    // Track the id of the last element inserted so we can restore the caret
                    // after it once all commands are in the DOM.
                    let lastNewElId: string | null = null;

                    for (const command of commands) {
                        if (!command) continue;
                        const textProdCommand = this.convertProductionCommandToText(command);
                        if (!textProdCommand) continue;
                        const newElId = v4();
                        const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(/\r\n|\n/g, '<br/>'), newElId);
                        if (productionItemElement) {
                            const before = content.substring(0, lastPointerPosition);
                            const after = content.substring(lastPointerPosition);
                            content = before + productionItemElement.outerHTML + after;
                            // Advance the pointer past the element we just inserted.
                            lastPointerPosition = before.length + productionItemElement.outerHTML.length;
                            lastNewElId = newElId;
                        }
                    }

                    const parsed = parseXml(content, 'text/html');
                    ed.html.set(parsed.body.innerHTML);

                    // Restore focus after the last inserted production command element.
                    const lastInsertedEl = lastNewElId ? ed.el.querySelector(`[id="${lastNewElId}"]`) : null;
                    if (lastInsertedEl) {
                        ed.selection.setAfter(lastInsertedEl);
                        ed.selection.restore();
                    } else {
                        ed.el.focus();
                    }

                    this.emitContentChange(ed.html.get());
                })
            )
            .subscribe();
    }

    /** Builds a blank command model of the requested type to pass into the dialog as initial data. */
    private initCommand(commandType: ProductionCommandType): ProductionCommand | ProductionCommand[] | null {
        const opts = ML2HtmlUtils.productionCommandsObjectOptions();
        switch (commandType) {
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
                // ANCHOR always opens with both Anchor and ReadRate fields in the same dialog.
                return [
                    { commandType: ProductionCommandType.ANCHOR, guid: null, [ProductionFieldName.TEXT]: '' } as AnchorCommand,
                    { commandType: ProductionCommandType.READRATE, guid: null, [ProductionFieldName.TEXT]: '' } as ReadRateCommand,
                ];
            case ProductionCommandType.DURATION:
                return { commandType, guid: null, [ProductionFieldName.DURATION]: null } as DurationCommand;
            case ProductionCommandType.NOTES:
                return { commandType, guid: null, [ProductionFieldName.TEXT]: '' } as NotesCommand;
            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT:
                return {
                    commandType,
                    guid: null,
                    // Default to the last option in the list (typically the "custom" / free-text option).
                    [ProductionFieldName.OPTION]: opts[opts.length - 1].id,
                    [ProductionFieldName.OPTION_DESCRIPTION]: null,
                    [ProductionFieldName.NAME]: null,
                    [ProductionFieldName.NUMBER]: null,
                    [ProductionFieldName.INCUE]: null,
                    [ProductionFieldName.OUTCUE]: null,
                    [ProductionFieldName.AT]: null,
                    [ProductionFieldName.TO]: null,
                    [ProductionFieldName.TIME_VALUE]: null,
                    [ProductionFieldName.TIME_FIELD]: ProductionFieldName.DURATION,
                } as TakeCommand | InsertCommand;
            default:
                return null;
        }
    }

    /**
     * Serialises a production command object into the bracketed text format expected by the
     * backend, e.g. `[TAKE :\r\nNAME:foo\r\nNUMBER:1]`. Returns an empty string if the command
     * has no meaningful content (so the caller can skip insertion).
     */
    private convertProductionCommandToText(command: ProductionCommand): string {
        const ls = this.languageSettings();
        let textLinesArray: string[] = [];
        switch (command.commandType) {
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
                // Single-line: "ANCHOR:text" or "READRATE:text". Null if the field is empty.
                textLinesArray = command[ProductionFieldName.TEXT] ? [`${ls[ProductionCommandsLangKey[command.commandType]]}:${command[ProductionFieldName.TEXT]}`] : null;
                break;
            case ProductionCommandType.DURATION:
                // Duration is stored as a raw timestamp; convert to HH:MM:SS:FF display format.
                textLinesArray = command[ProductionFieldName.DURATION]
                    ? [`${ls[ProductionCommandsLangKey[command.commandType]]}:${convertDurationToENPSClientFormat(command[ProductionFieldName.DURATION])}`]
                    : null;
                break;
            case ProductionCommandType.NOTES:
                // Flatten multi-line notes to a single line for the bracketed format.
                textLinesArray = command[ProductionFieldName.TEXT] ? [`${ls[ProductionCommandsLangKey[command.commandType]]}:${command[ProductionFieldName.TEXT].replace(/\r\n|\n/g, ' ')}`] : null;
                break;
            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT: {
                const opts = ML2HtmlUtils.productionCommandsObjectOptions();
                const lastOptionId = opts[opts.length - 1].id;
                textLinesArray = [
                    // First line: command label + option. If the user selected the custom option,
                    // append the free-text description; otherwise append the localised option name.
                    `${ls[ProductionCommandsLangKey[command.commandType]]}${
                        command[ProductionFieldName.OPTION] && command[ProductionFieldName.OPTION] !== lastOptionId
                            ? ' ' + ls[command[ProductionFieldName.OPTION]]
                            : `${
                                  command[ProductionFieldName.OPTION_DESCRIPTION] && command[ProductionFieldName.OPTION] === lastOptionId
                                      ? ` :${command[ProductionFieldName.OPTION_DESCRIPTION]}`
                                      : ' :'
                              }`
                    }`,
                    // Subsequent lines are optional fields; empty strings are filtered out at the end.
                    `${command[ProductionFieldName.NAME] ? `${ProductionFieldName.NAME}:${command[ProductionFieldName.NAME]}` : ''}`,
                    `${command[ProductionFieldName.NUMBER] ? `${ProductionFieldName.NUMBER}:${command[ProductionFieldName.NUMBER]}` : ''}`,
                    `${command[ProductionFieldName.INCUE] ? `${ProductionFieldName.INCUE}:${command[ProductionFieldName.INCUE]}` : ''}`,
                    `${command[ProductionFieldName.OUTCUE] ? `${ProductionFieldName.OUTCUE}:${command[ProductionFieldName.OUTCUE]}` : ''}`,
                    `${
                        command[ProductionFieldName.AT] && command[ProductionFieldName.AT] !== INITIAL_TIME_STAMP
                            ? `${ProductionFieldName.AT}:${convertDurationToENPSClientFormat(command[ProductionFieldName.AT])}`
                            : ''
                    }`,
                    `${
                        command[ProductionFieldName.TO] && command[ProductionFieldName.TO] !== INITIAL_TIME_STAMP
                            ? `${ProductionFieldName.TO}:${convertDurationToENPSClientFormat(command[ProductionFieldName.TO])}`
                            : ''
                    }`,
                    `${
                        command[ProductionFieldName.TIME_VALUE] && command[ProductionFieldName.TIME_VALUE] !== INITIAL_TIME_STAMP
                            ? `${command[ProductionFieldName.TIME_FIELD]}:${convertDurationToENPSClientFormat(command[ProductionFieldName.TIME_VALUE])}`
                            : ''
                    }`,
                ];
                break;
            }
            default:
                return '';
        }
        // Wrap non-empty lines in square brackets and join with CRLF.
        return textLinesArray && textLinesArray.length > 0 ? `[${textLinesArray.filter(val => !!val.trim()).join('\r\n')}]` : '';
    }

    /** Toggles the `--hidden` modifier on a details sub-element inside a MOS or production block. */
    private toggleDetails(parentElement: Element, detailsSelector: string): void {
        const detailsElement = parentElement.querySelector('.' + detailsSelector);
        if (detailsElement) {
            detailsElement.classList.toggle(detailsSelector + '--hidden');
            if (!detailsElement.classList.contains(detailsSelector + '--hidden')) {
                // The MOS video is emitted as an inert placeholder div, because a real media
                // element pins every throwaway parsed document in memory (see parseXml);
                // the real <video> is only materialized here, in the live document, on reveal.
                materializeNeutralizedMedia(detailsElement);
            }
        }
    }

    /**
     * Opens a CDK overlay containing `SidecarSelectorComponent` anchored to `anchorElement`.
     * When the user picks a layout, emits `mosItemOpen` with the MOS item and layout details.
     */
    openSidecarChooser(anchorElement: Element, metadata: { mosId: string; mosPluginId: string; storyGuid: string }): void {
        const { mosId, mosPluginId, storyGuid } = metadata;
        const positionStrategy = this.overlay.position().flexibleConnectedTo(anchorElement).withPositions(SIDECAR_SELECTOR_POSITIONS).withFlexibleDimensions(false).withPush(false);

        const overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'st-sidecar-selector-backdrop',
            disposeOnNavigation: true,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        const sidecarPortal = new ComponentPortal(SidecarSelectorComponent);
        const sidecarSelectorComponent = overlayRef.attach(sidecarPortal);

        sidecarSelectorComponent.instance.layoutSelected.subscribe(event => {
            this.mosItemOpen.emit({ mosId, mosPluginId, storyGuid, layout: event.layout, sidecar: event.sidecar });
            overlayRef.dispose();
        });

        overlayRef
            .keydownEvents()
            .pipe(
                filter(event => event.key === KeyName.Escape),
                take(1),
                map(() => overlayRef.dispose())
            )
            .subscribe();

        overlayRef
            .backdropClick()
            .pipe(
                take(1),
                map(() => overlayRef.dispose())
            )
            .subscribe();
    }

    /**
     * If the story is approved, shows a confirmation modal before executing `onConfirmCallback`.
     * On confirmation, emits `approvedChanged(false)` to un-approve the story, then runs the callback.
     * If the story is not approved, runs the callback immediately.
     */
    protected confirmApprovedStoryEdit(onConfirmCallback: () => void): void {
        if (this.approved()) {
            this.isProcessingContentChange = true;
            const ls = this.languageSettings();
            this.modalService
                .openModal({
                    title: ls.PSTWebModalWarningTitle,
                    content: ls.PSTWebConfirmEditApprovedStory,
                    yesString: ls.PSTWebOkButtonLabel,
                    noString: ls.PSTWebCancelButtonLabel,
                })
                .result.pipe(
                    take(1),
                    catchError(() => [ModalResponseCodes.CANCEL]),
                    finalize(() => {
                        this.isProcessingContentChange = false;
                    })
                )
                .subscribe(res => {
                    if (res === ModalResponseCodes.OK) {
                        this.approvedChanged.emit(false);
                        onConfirmCallback();
                    }
                });
        } else {
            onConfirmCallback();
        }
    }

    @HostListener('dragstart', ['$event'])
    onDragStart(event: DragEvent) {
        event.dataTransfer?.setData('text/plain', (event.target as HTMLElement).id);
        event.dataTransfer!.dropEffect = 'move';
        if ((event.target as HTMLElement).id) {
            this.isDraggingExistingCommand = true;
        }
    }
}
